import * as React from "react";
import toast, {
  Toaster as HotToaster,
  resolveValue,
  type Toast as HotToast,
} from "react-hot-toast";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "./icons";

import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { Spinner } from "./Spinner";

// Every toast in the app renders through <Toaster>, including the older `toast.success(...)`
// calls, so their look is controlled here. New code should call `notify`.

/** Older call sites sometimes pass an API error body ({ message, code }) instead of text. */
const toRenderable = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || typeof value === "boolean") {
    return null;
  }
  if (typeof value === "string" || typeof value === "number" || React.isValidElement(value)) {
    return value;
  }
  if (typeof value === "object") {
    const body = value as { message?: unknown; error?: unknown };
    const text = typeof body.message === "string" ? body.message : body.error;
    return typeof text === "string" && text ? text : "Something went wrong. Please try again.";
  }
  return String(value);
};

const typeIcon = (type: HotToast["type"]): React.ReactNode => {
  switch (type) {
    case "success":
      return <CircleCheck className="text-success" />;
    case "error":
      return <CircleAlert className="text-danger" />;
    case "loading":
      return <Spinner className="h-[18px] w-[18px] text-primary" />;
    default:
      return <Info className="text-info" />;
  }
};

const ToastCard: React.FC<{ t: HotToast }> = ({ t }) => {
  const message = toRenderable(resolveValue(t.message, t));
  const icon = t.icon && typeof t.icon !== "string" ? t.icon : typeIcon(t.type);
  return (
    <div
      data-ui="toast"
      {...t.ariaProps}
      className={cn(
        "pointer-events-auto flex w-[22.5rem] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-card border border-border bg-surface-raised py-3 pl-3.5 pr-2 text-sm text-foreground shadow-popover",
        t.visible
          ? "animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
          : "animate-out fade-out-0 zoom-out-95 fill-mode-forwards duration-150",
        t.className
      )}
    >
      <span aria-hidden="true" className="mt-px flex shrink-0 [&_svg]:h-[18px] [&_svg]:w-[18px]">
        {icon}
      </span>
      <div className="min-w-0 flex-1 break-words leading-5">{message}</div>
      {t.type !== "loading" && (
        <button
          type="button"
          onClick={() => toast.dismiss(t.id)}
          aria-label="Dismiss notification"
          className="-my-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

// Toasts stack bottom-right but start above the sticky "Unsaved changes" bars (sticky bottom-4,
// about 64px tall). Otherwise a toast lands under the cursor right after Save is clicked; it then
// pauses while hovered and covers that button.
const TOAST_BOTTOM_OFFSET = 88;

export const Toaster: React.FC = () => (
  <HotToaster
    position="bottom-right"
    gutter={10}
    containerStyle={{ zIndex: 11000, bottom: TOAST_BOTTOM_OFFSET }}
    toastOptions={{
      duration: 5000,
      success: { duration: 4000 },
      error: { duration: 7000, ariaProps: { role: "alert", "aria-live": "assertive" } },
      loading: { duration: Infinity },
    }}
  >
    {(t) => <ToastCard t={t} />}
  </HotToaster>
);

interface NotifyOptions {
  /** Second line under the title. */
  description?: React.ReactNode;
  /** One follow-up action; the toast closes after it runs. */
  action?: { label: string; onClick: () => void };
  /** Reuse an id to update a toast in place (e.g. loading → success). */
  id?: string;
  duration?: number;
}

let sequence = 0;

const buildContent = (
  title: React.ReactNode,
  id: string,
  options: NotifyOptions
): string | React.ReactElement => {
  if (!options.description && !options.action) {
    return typeof title === "string" || React.isValidElement(title) ? title : <>{title}</>;
  }
  return (
    <div className="space-y-1">
      <p className="font-medium">{title}</p>
      {options.description && <p className="text-muted-foreground">{options.description}</p>}
      {options.action && (
        <button
          type="button"
          className="pt-1 text-sm font-medium text-primary-text hover:underline focus-visible:outline-none focus-visible:underline"
          onClick={() => {
            options.action?.onClick();
            toast.dismiss(id);
          }}
        >
          {options.action.label}
        </button>
      )}
    </div>
  );
};

const show = (
  kind: "success" | "error" | "info" | "warning" | "loading",
  title: React.ReactNode,
  options: NotifyOptions = {}
) => {
  sequence += 1;
  const id = options.id || `notify-${sequence}`;
  const content = buildContent(title, id, options);
  const base = { id, duration: options.duration };
  switch (kind) {
    case "success":
      return toast.success(content, base);
    case "error":
      return toast.error(content, base);
    case "loading":
      return toast.loading(content, base);
    case "warning":
      return toast(content, {
        ...base,
        icon: <TriangleAlert className="text-warning" />,
        ariaProps: { role: "alert", "aria-live": "assertive" },
      });
    default:
      return toast(content, { ...base, icon: <Info className="text-info" /> });
  }
};

export const notify = {
  success: (title: React.ReactNode, options?: NotifyOptions) => show("success", title, options),
  info: (title: React.ReactNode, options?: NotifyOptions) => show("info", title, options),
  warning: (title: React.ReactNode, options?: NotifyOptions) => show("warning", title, options),
  loading: (title: React.ReactNode, options?: NotifyOptions) => show("loading", title, options),
  /** Accepts text or any thrown error; API errors show the server's message. */
  error: (error: unknown, options: NotifyOptions & { fallback?: string } = {}) =>
    show(
      "error",
      typeof error === "string" || React.isValidElement(error)
        ? error
        : getApiErrorMessage(error, options.fallback),
      options
    ),
  promise: <T,>(
    promise: Promise<T>,
    messages: { loading: string; success: string | ((value: T) => string); error?: string }
  ) =>
    toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: (err: unknown) => getApiErrorMessage(err, messages.error),
    }),
  dismiss: (id?: string) => toast.dismiss(id),
};
